import React, { useEffect, useMemo, useRef } from 'react';
import './customGuideline.less';
import { ReactComponent as ActiveStepIcon } from '../../../assets/images/svg/ActiveStep.svg';
import { ReactComponent as InActiveStepIcon } from '../../../assets/images/svg/InActiveStep.svg';
import { ReactComponent as CompletedStepIcon } from '../../../assets/images/svg/completedStep.svg';

interface GuidelineItem {
  title: string;
  description: string;
  step: number;
  completed: boolean;
  subStep?: any;
}

interface CustomGuidelineProps {
  items: GuidelineItem[];
  className?: string;
  showIdCard?: boolean;
  containerClassName?: string; // Add this prop to receive parent container class
}

const CustomGuideline: React.FC<CustomGuidelineProps> = ({
  items,
  className,
  containerClassName = 'guideline-container', // Default to guideline-container if not provided
}: CustomGuidelineProps): JSX.Element => {
  const activeRef = useRef<HTMLDivElement | null>(null);

  const itemsWithActiveStep = useMemo((): any => {
    let foundActive = false; // Flag to track if an active step has already been found
    return items?.map((item: GuidelineItem, index: number): any => {
      let isActive = false;
      // If a step is incomplete and no active step has been found yet, mark it as active
      if (!foundActive) {
        if (index > 0 && !items[index - 1]?.completed) {
          // Current step can't be active unless the previous step is completed
          isActive = false;
        } else if (item?.subStep && (!item?.completed || !item?.subStep?.completed)) {
          // Mark active if subStep is incomplete
          isActive = true;
        } else if (!item?.completed) {
          // Mark active if this step is the first incomplete step
          isActive = true;
        }
        // If the step is active, mark that an active step has been found
        if (isActive) {
          foundActive = true;
        }
      }

      return {
        ...item,
        active: isActive, // Ensure only one active step at a time
      };
    });
  }, [items]);

  useEffect((): void => {
    if (activeRef.current) {
      requestAnimationFrame((): void => {
        const container = document.querySelector(`.${containerClassName}`);
        const activeElement = activeRef.current;

        if (container && activeElement) {
          const containerHeight = container.clientHeight;
          const elementTop = activeElement.offsetTop;
          const elementHeight = activeElement.clientHeight;

          const scrollTo = elementTop - (containerHeight / 2) + (elementHeight / 2);

          container.scrollTo({
            top: scrollTo,
            behavior: 'smooth',
          });
        }
      });
    }
  }, [items, containerClassName]);

  const getStepIcon = (item: GuidelineItem & { active: boolean }): any => {
    if (item?.active) {
      // Show ActiveStepIcon if the item is active, even if it's marked as completed
      return <ActiveStepIcon />;
    } else if (item?.completed) {
      // Only show CompletedStepIcon if the item is completed and not active
      return <CompletedStepIcon />;
    } else {
      // Show InActiveStepIcon if the item is neither active nor completed
      return <InActiveStepIcon />;
    }
  };

  return (
    <div className={`custom-guideline ${className}`}>
      {itemsWithActiveStep?.map((item: any, index: number): any => (
        <React.Fragment key={index}>
          {index > 0 && <div className="connector-line" />}
          <div className="guideline-item-box"
          ref={item?.active ? activeRef : null}
          >
            <div className={`guideline-item-content ${item.active ? 'active-step' : ''}`}>
              <div className="guideline-item-text">
                <div className="margin-left-9">
                  {getStepIcon(item)}
                </div>
                <div className="guideline-text-content">
                  <h4 className="guideline-step-title">{item?.title}</h4>
                  <p className="guideline-step-description">{item?.description}</p>
                </div>
              </div>
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

export default CustomGuideline;
